import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Hex } from 'viem';
import { Address } from '../data/address-config';
import { GachaTeamRegistryABI } from '../generated/abi/gachaTeamRegistry';
import { TEAM_SIZE } from '../types/constants';
import { OnchainStateService } from './onchain-state.service';
import { TeamService } from './team.service';
import { Wallet } from './wallet';

// 'unknown'    — not yet evaluated (existing players with a team stay here)
// 'checking'   — reading chain to decide whether repair is needed
// 'migrating'  — migrate() txs in flight
// 'failed'     — detection, migrate, or team seeding failed; retryable, setup stays gated
// 'not-needed' — genuine new player / nothing to bring forward → normal gacha/setup
// 'done'       — imported and/or team seeded, state re-hydrated
export type MigrationStatus =
  | 'unknown' | 'checking' | 'migrating' | 'failed' | 'not-needed' | 'done';

// migrate() copies a variable amount of storage (scales with owned mons), so we
// estimate + buffer; this only backstops the rare case the estimate RPC throws.
const MIGRATE_GAS_FALLBACK = 3_000_000n;

// Loop guard on the PREVIOUS_REGISTRY walk — the real chain is a handful of deploys.
const MAX_REGISTRY_CHAIN = 8;

/**
 * Imports a returning player's progress from an older GachaTeamRegistry into the
 * current one, and makes sure the account ends up with a team. Runs automatically
 * when a previously-used wallet loads with no team, and gates the gacha/setup flow
 * (`suppressSetup`) until it resolves so a returning player can't roll a fresh
 * account and orphan their old progress.
 */
@Injectable({ providedIn: 'root' })
export class RegistryMigrationService {
  private readonly onchainState = inject(OnchainStateService);
  private readonly team = inject(TeamService);
  private readonly wallet = inject(Wallet);

  private readonly _status = signal<MigrationStatus>('unknown');
  readonly status = this._status.asReadonly();

  // The single gate the UI reads to hide gacha/setup: we're actively checking,
  // migrating, or parked on a retryable failure.
  readonly suppressSetup = computed(() => {
    const s = this._status();
    return s === 'checking' || s === 'migrating' || s === 'failed';
  });

  // True while a no-team account's migration outcome is still undetermined.
  // Lets the onboarding auto-open hold off so it doesn't pop welcomes/gacha
  // ahead of (or during) a silent migration. Existing players (hasTeam true)
  // resolve to false immediately so their normal onboarding is unaffected.
  readonly decisionPending = computed(() => {
    const s = this._status();
    if (s === 'checking' || s === 'migrating' || s === 'failed') return true;
    if (s === 'unknown') {
      return !this.onchainState.isLoaded() || this.onchainState.hasTeam() === false;
    }
    return false;
  });

  // Address we've kicked off evaluation for — a plain field (not a signal) so the
  // trigger effect doesn't depend on it and can't loop on its own status writes.
  private evaluatedAddress: Hex | null = null;

  constructor() {
    effect(() => {
      const address = this.onchainState.activeAddress();
      const loaded = this.onchainState.isLoaded();
      const hasTeam = this.onchainState.hasTeam();

      if (!address) {
        this.evaluatedAddress = null;
        this._status.set('unknown');
        return;
      }
      // Only accounts that load with no team on the new registry are candidates.
      if (loaded && hasTeam === false && this.evaluatedAddress !== address) {
        this.evaluatedAddress = address;
        this.evaluate(address);
      }
    });
  }

  // Re-run the whole decision (detection + migrate). The failure-state Retry uses
  // this, so a player misclassified by a transient read error self-corrects.
  retry(): void {
    const address = this.evaluatedAddress ?? this.onchainState.activeAddress();
    if (address) this.evaluate(address);
  }

  private async evaluate(address: Hex): Promise<void> {
    this._status.set('checking');
    try {
      const nonce = await this.wallet.publicClient.getTransactionCount({ address });
      if (this.isStale(address)) return;
      // Mons only ever arrive via a tx the player sent (first-roll, roll, migrate),
      // so a never-used wallet is genuinely new and has nothing to repair.
      if (nonce === 0) {
        this._status.set('not-needed');
        return;
      }

      let repaired = false;

      // A full team's worth of mons means the account is already viable here, and
      // migrate() overwrites the profile word wholesale — don't import over it.
      if ((this.onchainState.ownedMonIndices()?.length ?? 0) < TEAM_SIZE) {
        const hops = await this.findMigrationHops(address);
        if (this.isStale(address)) return;
        if (hops.length > 0) {
          await this.runMigrate(address, hops);
          if (this.isStale(address)) return;
          repaired = true;
        }
      }

      // Mons but no team is the shape that silently disables every play gate.
      repaired = (await this.seedTeamIfPossible(address)) || repaired;
      if (this.isStale(address)) return;

      this._status.set(repaired ? 'done' : 'not-needed');
    } catch (err) {
      console.warn('[migration] evaluation failed', err);
      this._status.set('failed');
    }
  }

  // migrate() only ever imports one generation, so progress left more than one
  // deploy back needs each intermediate registry replayed. Walks PREVIOUS_REGISTRY
  // until it finds a generation still holding the player's mons, then returns the
  // registries to call, oldest-first; empty when there's nothing to bring forward.
  private async findMigrationHops(player: Hex): Promise<Hex[]> {
    const chain: Hex[] = [Address.GACHA_TEAM_REGISTRY];
    const seen = new Set<string>([Address.GACHA_TEAM_REGISTRY.toLowerCase()]);
    let source = -1;

    while (chain.length < MAX_REGISTRY_CHAIN) {
      const prev = await this.wallet.publicClient.readContract({
        address: chain[chain.length - 1],
        abi: GachaTeamRegistryABI,
        functionName: 'PREVIOUS_REGISTRY',
      });
      if (BigInt(prev) === 0n || seen.has(prev.toLowerCase())) break;
      seen.add(prev.toLowerCase());
      chain.push(prev);

      const balance = await this.wallet.publicClient.readContract({
        address: prev,
        abi: GachaTeamRegistryABI,
        functionName: 'balanceOf',
        args: [player],
      });
      if (balance > 0n) {
        source = chain.length - 1;
        break;
      }
    }
    if (source < 0) return [];

    // Each generation above the source pulls from the one below it, so walk up.
    // A hop already imported would revert AlreadyMigrated — skip it.
    const candidates = chain.slice(0, source).reverse();
    const imported = await Promise.all(
      candidates.map((address) =>
        this.wallet.publicClient.readContract({
          address,
          abi: GachaTeamRegistryABI,
          functionName: 'migrated',
          args: [player],
        }),
      ),
    );
    return candidates.filter((_, i) => !imported[i]);
  }

  private async runMigrate(address: Hex, hops: readonly Hex[]): Promise<void> {
    this._status.set('migrating');
    // Strictly sequential: each hop reads the generation below it, so it can only
    // run once its predecessor has landed.
    for (const registry of hops) {
      let gas = MIGRATE_GAS_FALLBACK;
      try {
        const estimate = await this.wallet.publicClient.estimateContractGas({
          address: registry,
          abi: GachaTeamRegistryABI,
          functionName: 'migrate',
          account: address,
        });
        gas = (estimate * 13n) / 10n;
      } catch {
        // Estimate failed (transient RPC, or a hop whose source hasn't landed yet).
      }

      await this.wallet.sendRealtimeRawTransaction({
        address: registry,
        abi: GachaTeamRegistryABI,
        functionName: 'migrate',
        ...this.wallet.burnerTx,
        gas,
      });
      if (this.isStale(address)) return;
    }

    // Re-hydrate so owned mons / teams populate and hasTeam flips true.
    await this.onchainState.loadStateForAddress(address);
  }

  // Owning mons without a team leaves the account signed in but unable to battle,
  // and nothing else in the app creates a team outside first-roll onboarding.
  private async seedTeamIfPossible(address: Hex): Promise<boolean> {
    if (this.onchainState.orderedSlots().length > 0) return false;
    const owned = this.onchainState.ownedMonIndices() ?? [];
    if (owned.length < TEAM_SIZE) return false;
    await this.team.setTeam(0n, owned.slice(0, TEAM_SIZE));
    return true;
  }

  // Bail out of an async chain whose wallet switched out from under it.
  private isStale(address: Hex): boolean {
    return this.evaluatedAddress !== address;
  }
}
